import { vec2 } from 'gl-matrix';
import { atom } from 'recoil';
import { DoseId } from '~/domain/changes/ids';
import {
    DEFAULT_DOSE_OPACITY_VALUE,
    DEFAULT_MIN_DOSE_RANGE,
    DEFAULT_MAX_DOSE_RANGE,
} from '~/engine/data/dose/DoseDistribution';

export const doseVisibilityState = atom<boolean>({
    key: 'doseVisibilityState',
    default: true,
});

/** How opaque every drawn dose is, from 0 (unseen) to 1 (fully opaque). */
export const doseOpacityState = atom<number>({
    key: 'doseOpacityState',
    default: DEFAULT_DOSE_OPACITY_VALUE,
});

/** One step of a dose opacity control, as a fraction of fully opaque. */
export const DOSE_OPACITY_STEP = 0.1;

/** `value` within the dose opacity's range of 0 to 1. */
export function doseOpacityWithinRange(value: number): number {
    return Math.min(1, Math.max(0, value));
}

/** `value` on the dose opacity's steps, within its range of 0 to 1. */
export function doseOpacityOnStep(value: number): number {
    return doseOpacityWithinRange(
        Math.round(value / DOSE_OPACITY_STEP) * DOSE_OPACITY_STEP
    );
}

/**
 * The selected dose, held as a domain {@link DoseId} rather than a live
 * {@link DoseMatrix} reference.
 *
 * UI state points at domain entities by id; it never owns them. Storing
 * the entity meant `dangerouslyAllowMutability: true` and left Recoil
 * holding a mutable aggregate member whose changes it could not observe.
 * Use `useCurrentDose()` to resolve the entity, or — preferably —
 * `useDoseDetailView()` / `useDoseListView()` to read a projection.
 */
export const currentDoseIdState = atom<DoseId | null>({
    key: 'currentDoseId',
    default: null,
});

/**
 * Live colorwash window driven by the slider, normalized to 0..1 of max
 * dose. Deliberately separate from the committed per-dose window in
 * `doseDisplaySettingsState`: the slider writes here on every drag frame,
 * while the committed value is written once at the end of the gesture
 * (`useDose().commitDoseRange`) so metadata consumers do not re-render
 * mid-drag.
 */
export const selectedDoseRangeState = atom<vec2>({
    key: 'selectedDoseRangeState',
    default: vec2.fromValues(DEFAULT_MIN_DOSE_RANGE, DEFAULT_MAX_DOSE_RANGE),
});
